import { Button } from "components/Button/Button";
import { requiredValidator } from "components/Form/Form.utils";
import { StoreProviderForm } from "components/Form/StoreProviderForm";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { Form } from "components/Form/components/Form/Form";
import { TextareaFormComponent } from "components/Form/components/Textarea/TextareaFormComponent";
import { IconCollection } from "components/Icon/IconCollection";
import {
    Action,
    ActionData,
    ActionSignature,
} from "containers/Resources/components/CommonActions/Action";
import {
    getInitialModal,
    getRecordOrContainerIsGitEnabled,
    ResetToStatusInGitAction,
} from "containers/Resources/screens/ResourcesSingle/components/GitAction/GitAction";
import { showEmptyCommitModal } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/showEmptyCommitModal";
import {
    bulkCommit,
    isGitActionButtonDisabled,
} from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { Field, Form as FinalForm } from "react-final-form";
import styles from "../GitAction.module.scss";

const CommitAction: Action = async (data, dispatch, history) => {
    const uncommittedData = data?.uncommittedData;
    const uncommittedRecordData = uncommittedData[data.resourceData.name];

    if (!uncommittedRecordData) {
        return showEmptyCommitModal();
    }
    return new Promise(async (resolve, reject) => {
        const modal = getInitialModal(
            `Commit changes for ${data.resourceType} "${data.resourceData.name}"`
        );

        const closeModal = () => modal.destroy();

        const commit = (formData) => {
            const formValues = {
                records: [{ name: data.resourceData.name }],
                ...formData,
            };
            bulkCommit({
                formData: formValues,
                actionData: data,
                closeModal,
                uncommittedData,
                path: "commit",
                resourcePath: `${data.resourceType}/${data.resourceData.id}`,
            });
        };

        const form = (
            <StoreProviderForm>
                <FinalForm
                    onSubmit={commit}
                    initialValues={{
                        commit_message: uncommittedRecordData.message,
                    }}
                >
                    {({ handleSubmit, hasValidationErrors, submitSucceeded }) => {
                        return (
                            <Form onSubmit={handleSubmit}>
                                <div className={`${submitSucceeded ? styles.disabledForm : ""}`}>
                                    <Form.Row>
                                        <Field
                                            name="commit_message"
                                            component={TextareaFormComponent}
                                            fieldProps={{
                                                label: "Commit message:",
                                            }}
                                            fieldAttr={{
                                                placeholder: "enter commit message",
                                                required: true,
                                            }}
                                            validate={requiredValidator}
                                        />
                                    </Form.Row>
                                </div>
                                <Form.Row>
                                    <CancelButton onClick={() => closeModal()} />
                                    <Button
                                        onClick={() => {
                                            handleSubmit();
                                            closeModal();
                                        }}
                                        disabled={hasValidationErrors}
                                        loading={submitSucceeded}
                                        variation="primary"
                                        label={"Commit"}
                                        icon={IconCollection.commit}
                                    />
                                </Form.Row>
                            </Form>
                        );
                    }}
                </FinalForm>
            </StoreProviderForm>
        );

        modal.update((previousConfig) => ({
            ...previousConfig,
            content: form,
        }));
    });
};

export const commitActionKey = "splitCommit";

export const commitActionSignature: ActionSignature = {
    key: commitActionKey,
    action: CommitAction,
    label: () => "Commit",
    icon: () => IconCollection.commit,
    hasAfterAction: true,
    isDropdown: () => true,
    dropdownContent: ({ actionData, dispatch, history, isDisabled, saveForm }) => {
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                }}
            >
                <Button
                    key={"resetToStatusInGit"}
                    label={"Reset to status in git"}
                    icon={IconCollection.undo}
                    variation="clear"
                    className="wide"
                    onClick={() => ResetToStatusInGitAction(actionData, dispatch, history)}
                />
            </div>
        );
    },
    hasDropdownContentAfterAction: true,
    omitWhen: (data: ActionData) =>
        !getRecordOrContainerIsGitEnabled(data?.resourceType, data?.resourceData) ||
        data?.resourceData?.track_in_git === false,
    disableWhen: (data: ActionData) =>
        isGitActionButtonDisabled(data) ||
        !data?.uncommittedData[data?.resourceData?.name] ||
        data?.isGitLocked ||
        false,
};
